import React, { useCallback } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import GettextFactory from '../../../util/GettextFactory';
import { useSqPrefillPreference } from '../hooks/useSqPrefillPreference';
import {
    useGetAutoApplyEligiblePastAnswersQuery,
    useGetSqPrefillPreferenceQuery,
} from './__generated__/PastAnswers.generated';
import {
    PAST_ANSWERS_PAGE_SIZE,
    PastAnswersPageContent,
    type PastAnswersPageConnection,
} from './PastAnswersPageContent';

export const useShouldIncludeHostedSQ = (): boolean => {
    const { getGroup } = useProctorGroups();
    const value = getGroup('ia_afm_iqv2_prefill_tst')?.value;
    return value === 1 || value === 2;
};

/** SQ-specific queries and preference mutation for the shared Past Answers UI. */
export const SqPastAnswersPage: React.FC = () => {
    const gt = GettextFactory();
    const shouldIncludeHostedSQ = useShouldIncludeHostedSQ();
    const {
        data: prefillData,
        error: prefillError,
        refetch: refetchPrefill,
    } = useGetSqPrefillPreferenceQuery({ fetchPolicy: 'cache-first' });
    const {
        usePastAnswers,
        handleUsePastAnswersChange,
        error: mutationError,
    } = useSqPrefillPreference(prefillData?.autoApplyInfo?.sqPastAnswerPrefillPreference);
    const {
        data,
        loading,
        networkStatus,
        error: pastAnswersError,
        refetch: refetchPastAnswers,
        fetchMore,
    } = useGetAutoApplyEligiblePastAnswersQuery({
        variables: { first: PAST_ANSWERS_PAGE_SIZE, shouldIncludeHostedSQ },
        notifyOnNetworkStatusChange: true,
        ssr: false,
    });

    const apiConnection = data?.autoApplyEligiblePastAnswers;
    const connection: PastAnswersPageConnection | undefined = apiConnection
        ? {
              edges: apiConnection.edges.map((edge) => edge.node),
              pageInfo: apiConnection.pageInfo,
              totalCount: apiConnection.totalCount,
          }
        : undefined;

    const onFetchMore = useCallback(
        (first: number, after: string) =>
            fetchMore({ variables: { first, after, shouldIncludeHostedSQ } }),
        [fetchMore, shouldIncludeHostedSQ],
    );
    const onRetrySource = useCallback(() => {
        if (pastAnswersError) void refetchPastAnswers();
        if (prefillError) void refetchPrefill();
        if (mutationError) void handleUsePastAnswersChange(!usePastAnswers);
    }, [
        pastAnswersError,
        prefillError,
        mutationError,
        refetchPastAnswers,
        refetchPrefill,
        handleUsePastAnswersChange,
        usePastAnswers,
    ]);

    return (
        <PastAnswersPageContent
            connection={connection}
            usePastAnswers={usePastAnswers}
            onUsePastAnswersChange={handleUsePastAnswersChange}
            headerTitle={gt.gettext('Application questions')}
            toggleLabel={gt.gettext('Autofill application questions')}
            fetching={loading || networkStatus === NetworkStatus.fetchMore}
            sourceError={Boolean(pastAnswersError || prefillError || mutationError)}
            onRetrySource={onRetrySource}
            onFetchMore={onFetchMore}
        />
    );
};
